import React from "react";
import "./WhyChooseUs.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileContract,
  faChartPie,
  faUserShield,
  faScaleBalanced,
  faHeadset,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const cards = [
  {
    icon: faFileContract,
    title: "Scoped in writing",
    text: "Proposals list deliverables, fees, and assumptions. Change orders happen when scope shifts—not through surprise invoices.",
  },
  {
    icon: faChartPie,
    title: "Reporting you can use",
    text: "Dashboards connect spend to outcomes you care about, with caveats when data is modeled or incomplete.",
  },
  {
    icon: faUserShield,
    title: "Privacy-aware setup",
    text: "Tags, pixels, and consent tools follow your privacy policy—not a “track everything by default” approach.",
  },
  {
    icon: faScaleBalanced,
    title: "Honest claims",
    text: "We help you avoid exaggerated promises in ads and landing pages—better for customers and platform approvals.",
  },
  {
    icon: faHeadset,
    title: "Direct communication",
    text: "A clear point of contact and regular touchpoints. No black-box “trust us” marketing.",
  },
  {
    icon: faCircleCheck,
    title: "Corrections welcome",
    text: "If copy, targeting, or tracking drifts out of date, we fix it and document what changed.",
  },
];

const WhyChooseUs = () => {
  const navigate = useNavigate();

  return (
    <section className="why" id="why-choose" aria-labelledby="why-heading">
      <div className="why__wrap">
        <div className="why__left">
          <motion.p
            className="why__kicker"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Why clients stay
          </motion.p>

          <motion.h2
            id="why-heading"
            className="why__title"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.05 }}
          >
            Marketing partners who tell the truth
          </motion.h2>

          <motion.p
            className="why__sub"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Built for teams that want growth without cutting corners on compliance or customer trust. We are not a
            substitute for legal counsel or your in-house brand team.
          </motion.p>

          <motion.ul
            className="why__pillars"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <li>No fake case-study metrics</li>
            <li>No “guaranteed” rankings or ROAS</li>
            <li>Clear disclosure when we earn referral fees</li>
          </motion.ul>

          <motion.button
            type="button"
            className="why__cta"
            onClick={() => navigate("/contact")}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Start a conversation →
          </motion.button>
        </div>

        <div className="why__right">
          {cards.map((c, idx) => (
            <motion.article
              key={c.title}
              className="why__card"
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
            >
              <div className="why__icon" aria-hidden="true">
                <FontAwesomeIcon icon={c.icon} />
              </div>
              <h3 className="why__cardTitle">{c.title}</h3>
              <p className="why__cardText">{c.text}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
